import React, { useCallback, useMemo, useState } from 'react';
import { __ } from '@wordpress/i18n';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { klona } from 'klona';

// store
import { storeSet, useStoreValue } from '../../store';

// service
import UsersService from '../../service/users-service';
import ProjectService from '../../service/project-service';
import { queryKeys } from '../../lib/query-client';

// permissions
import { PROJECT_ROLES, ROLE_LABELS } from '../../helpers/projectRoles';

// components
import FormField from '../FormField';
import ButtonFlat from '../ButtonWrapper/components/ButtonFlat';
import ButtonFormSubmit from '../ButtonWrapper/components/ButtonFormSubmit';
import ModalDialog from '../ModalDialog';
import UserAvatar from '../UserAvatar';

/** The roles a project invitation can carry (`ProjectTeamRole`). */
const ROLE_OPTIONS = Object.values(PROJECT_ROLES).map(role => ({
    value: role,
    label: ROLE_LABELS[role],
}));

const ModalModifyTeam = ({
                             projectId = 0,
                             headerText = __('Modifica Team', 'soundbetween')
                         }) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    // /users/list and /user/me both key a user by `id`, so the signed-in user can
    // be recognised among the search results and dropped — inviting yourself to
    // your own project is never a real choice.
    const currentUserId = useStoreValue('userData')?.id || null;

    // GET /projects/{id}/team returns invitation rows, and the only statuses are
    // PENDING and ACCEPTED — so this one list covers both "already a member" and
    // "already invited, waiting on them". Same query key the rest of the app
    // uses, so an open modal reads the cache instead of refetching.
    const { data: team = [] } = useQuery({
        queryKey: queryKeys.projectTeam(projectId),
        queryFn: () => ProjectService.getProjectTeamAsync(projectId),
        enabled: Boolean(projectId) && modalVisible,
    });

    // `userId` is null on invitations sent to an email address that has no
    // account yet; those cannot collide with a search result, so drop them.
    const alreadyInvitedIds = useMemo(() => {
        const ids = new Set((team || []).map(member => member.userId).filter(Boolean));

        if (currentUserId) ids.add(currentUserId);

        return ids;
    }, [team, currentUserId]);
    const {
        control,
        handleSubmit,
        formState: { isValid }
    } = useForm({
        mode: 'onChange',
        defaultValues: {
            teamMembers: [],
            role: PROJECT_ROLES.MUSICIAN,
            notes: ''
        }
    });

    const onSubmit = useCallback((formData) => {
        const { teamMembers, role, notes } = klona({ ...formData });
        // The role decides what each member may do in the Comm Center and on the
        // cue sheet, so it is picked here instead of being hardcoded.
        const invitations = teamMembers.map(userId => ({
            user_id: userId,
            role,
            note: notes || null,
        }));

        storeSet('createInvitationsBulkForProject', projectId, { invitations });
    }, [projectId]);

    const handleCreateNew = () => {
        setModalVisible(true);
    }

    // Search function for team members
    const searchTeamMembers = useCallback(async (keyword) => {
        try {
            const response = await UsersService.getUsersAsync([['name', keyword]]);

            // Transform response to match expected format
            if (response) {
                return response
                    .filter(member => !alreadyInvitedIds.has(member.id))
                    .map(member => ({
                        ...member,
                        value: member.id,
                        label: `${member.name || ''} ${member.surname || ''}`.trim() || member.email
                    }));
            }

            return [];
        } catch (error) {
            console.error('Error searching team members:', error);
            return [{
                value: 'no_matches',
                label: __('Nessun risultato trovato', 'soundbetween')
            }];
        }
    }, [alreadyInvitedIds]);

    // Custom render for selected items with avatars
    const renderSelectedUser = (user, handleRemove) => {
        return (
            <>
                <UserAvatar
                    url={user.profile_picture_url}
                    name={user.label}
                />
                <span className="multiSelectDropdown__selectedItemLabel">
                    {user.label}
                </span>
                <button
                    type="button"
                    onClick={() => handleRemove(user.value)}
                    className="multiSelectDropdown__removeBtn"
                    aria-label={__('Rimuovi utente', 'soundbetween')}
                />
            </>
        );
    };

    return (
        <>
            <ButtonFlat
                onClick={handleCreateNew}>
                {__('Modifica', 'soundbetween')}
            </ButtonFlat>
            {modalVisible
                ? <ModalDialog
                    additionalClasses={['modalCreateProject']}
                    header={headerText}
                    visible={modalVisible}
                    onHide={() => {
                        if (!modalVisible) return;
                        setModalVisible(false);
                    }}>
                    <form className="appForm formInModal" onSubmit={handleSubmit(onSubmit)}>
                        <FormField
                            type="multiselect"
                            name="teamMembers"
                            label={__('Seleziona Membri del Team', 'soundbetween')}
                            control={control}
                            searchFn={searchTeamMembers}
                            labelKey="label"
                            valueKey="value"
                            placeholder={__('Cerca membri...', 'soundbetween')}
                            displayMode="count"
                            showClearAll={true}
                            minSearchLength={2}
                            renderSelectedItem={renderSelectedUser}
                            rules={{
                                required: __('Seleziona almeno un membro', 'soundbetween')
                            }}
                        />

                        <FormField
                            type="select"
                            name="role"
                            label={__('Ruolo nel progetto', 'soundbetween')}
                            control={control}
                            options={ROLE_OPTIONS}
                            description={__('Determina cosa può fare nel progetto: produttore e direttore assegnano e approvano le attività, il musicista lavora sulle cue, il committente commenta.', 'soundbetween')}
                        />

                        <FormField
                            type="textarea"
                            name="notes"
                            rows={7}
                            label={__('Aggiungi note', 'soundbetween')}
                            control={control}
                            config={{ required: __('È obbligatorio', 'soundbetween') }}
                        />

                        <div className="appFormActions">
                            <ButtonFlat size="lg" type="button" onClick={() => {
                                if (!modalVisible) return;
                                setModalVisible(false);
                            }}>
                                {__('Annulla', 'soundbetween')}
                            </ButtonFlat>
                            <ButtonFormSubmit disabled={loading || !isValid}>
                                {__('Salva', 'soundbetween')}
                            </ButtonFormSubmit>
                        </div>
                    </form>
                </ModalDialog> : null}
        </>
    )
}

export default ModalModifyTeam;